import { useCallback } from "react";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import type { FormValidationErrors } from "~/shared/hooks/use-form-validation.ts";
import { useFormValidation } from "~/shared/hooks/use-form-validation.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { isPastShipDate } from "~/shared/utils/date-helpers.ts";
import { isValidPhoneNumber } from "~/shared/utils/string-fns.ts";
import { validateHandlingUnitItem } from "~/shared/utils/validate-handling-unit-item.ts";

const MAX_TEXT_LENGTH = 50;

function isMissing(value: string | null | undefined): boolean {
	return !value?.trim();
}

function isOverLimit(value: string | null | undefined): boolean {
	return (value?.length ?? 0) > MAX_TEXT_LENGTH;
}

function validatePickupFields(
	fields: Set<string>,
	pickup: SelectedAddressBookItems["pickup"],
	shipmentDate: string | undefined,
	isInternal: boolean,
): void {
	const detail = pickup?.details.find((d) => d.stopType === 1);
	if (isMissing(pickup?.companyName)) fields.add("pickupName");
	if (isOverLimit(pickup?.companyName)) fields.add("pickupNameLength");
	if (isMissing(pickup?.address1)) fields.add("pickupAddress1");
	if (isOverLimit(pickup?.address1)) fields.add("pickupAddress1Length");
	if (isOverLimit(pickup?.address2)) fields.add("pickupAddress2Length");
	if (isMissing(shipmentDate)) fields.add("pickupShipmentDate");
	if (isPastShipDate(shipmentDate, isInternal))
		fields.add("pickupShipmentDatePast");
	if (isMissing(pickup?.postalCode)) fields.add("pickupPostalCode");
	if (isMissing(detail?.hoursOpen)) fields.add("pickupHoursFrom");
	if (isMissing(detail?.hoursClose)) fields.add("pickupHoursTo");
	if (isOverLimit(detail?.contactName)) fields.add("pickupContactNameLength");
}

function validateDeliveryFields(
	fields: Set<string>,
	delivery: SelectedAddressBookItems["delivery"],
): void {
	const detail = delivery?.details.find((d) => d.stopType === 2);
	if (isMissing(delivery?.companyName)) fields.add("deliveryName");
	if (isOverLimit(delivery?.companyName)) fields.add("deliveryNameLength");
	if (isMissing(delivery?.address1)) fields.add("deliveryAddress1");
	if (isOverLimit(delivery?.address1)) fields.add("deliveryAddress1Length");
	if (isOverLimit(delivery?.address2)) fields.add("deliveryAddress2Length");
	if (isMissing(delivery?.postalCode)) fields.add("deliveryPostalCode");
	if (!isValidPhoneNumber(detail?.contactPhone ?? ""))
		fields.add("deliveryContactPhone");
	if (isMissing(detail?.hoursOpen)) fields.add("deliveryHoursFrom");
	if (isMissing(detail?.hoursClose)) fields.add("deliveryHoursTo");
	if (isOverLimit(detail?.contactName)) fields.add("deliveryContactNameLength");
}

function validateTenderItem(
	item: QuoteStateType["items"][number],
	isVendor: boolean,
): Set<string> {
	const invalidFields = validateHandlingUnitItem(item, false);
	// Vendors may leave the freight class blank — the backend estimated class is
	// sent on their behalf, so class is not a required field for vendor tenders.
	if (isVendor) invalidFields.delete("class");
	if (isMissing(item.description)) invalidFields.add("description");
	if (item.isHazmat) {
		if (item.pieceCount == null || item.pieceCount <= 0)
			invalidFields.add("pieceCount");
		if (isMissing(item.unNumber)) invalidFields.add("unNumber");
		if (isMissing(item.properShippingName))
			invalidFields.add("properShippingName");
		if (!item.hazmatClass?.id) invalidFields.add("hazmatClass");
		if (!item.packingGroupId?.id) invalidFields.add("packingGroupId");
	}
	return invalidFields;
}

function validateTenderState(
	quoteState: QuoteStateType,
	isInternal: boolean,
	selectedAddressBookItems: SelectedAddressBookItems,
	isVendor: boolean,
): FormValidationErrors {
	const fields = new Set<string>();
	const items: Record<number, Set<string>> = {};

	// Customer – only required for internal users
	if (
		isInternal &&
		(!quoteState.customer?.id || quoteState.customer.id === 0)
	) {
		fields.add("customer");
	}

	validatePickupFields(
		fields,
		selectedAddressBookItems.pickup,
		quoteState.shipmentDate,
		isInternal,
	);
	validateDeliveryFields(fields, selectedAddressBookItems.delivery);

	let hasHazmat = false;
	for (const item of quoteState.items) {
		const invalidFields = validateTenderItem(item, isVendor);
		if (invalidFields.size > 0) items[item.id] = invalidFields;
		if (item.isHazmat) hasHazmat = true;
	}

	// Emergency contact details are required for hazmat shipments, except for
	// vendors — they are optional (not required) for vendor tenders.
	if (hasHazmat && !isVendor) {
		if (isMissing(quoteState.emergencyContactName))
			fields.add("emergencyContactName");
		if (!isValidPhoneNumber(quoteState.emergencyContactPhone ?? ""))
			fields.add("emergencyContactPhone");
	}

	// Shipment Value is optional, but once a positive value is entered the
	// New/Used (insuranceProductTypeId) selection becomes required.
	if (
		quoteState.insuranceValue != null &&
		quoteState.insuranceValue > 0 &&
		quoteState.insuranceProductTypeId == null
	) {
		fields.add("insuranceProductTypeId");
	}

	return { fields, items };
}

export const useTenderValidation = () => {
	const validationFn = useCallback(
		(
			quoteState: QuoteStateType,
			isInternal: boolean,
			selectedAddressBookItems: SelectedAddressBookItems,
			isVendor = false,
		) =>
			validateTenderState(
				quoteState,
				isInternal,
				selectedAddressBookItems,
				isVendor,
			),
		[],
	);

	return useFormValidation(validationFn);
};
